import React, {useEffect, useMemo, useReducer, useState} from "react";
import moment from "moment";
import withStyles from "@material-ui/core/styles/withStyles";
import LinearProgress from "@material-ui/core/LinearProgress";

import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import Card from "components/Card/Card.jsx";
import CardBody from "components/Card/CardBody.jsx";
import CardHeader from "components/Card/CardHeader.jsx";
import Accordion from "components/Accordion/Accordion.jsx";
import Button from "components/CustomButtons/Button.jsx";
import EstadoParticipacaoModulo from "components/EstadoParticipacaoModulo";
import EstadoUtilizador from "../../components/EstadoUtilizador";
import SweetAlert from "components/SweetAlertWrapper";
import PaginaFormacao from "./PaginaFormacao";
import CaixaFiltrosAcontecimento, {ProviderFiltrosAcontecimento, WidgetsFiltrosAcontecimento} from "../../components/FiltrosAcontecimento/FiltrosAcontecimento";
import ReactTable from "components/ReactTableWrapper";
import useDebounce from "../../functions/useDebounce";
import AcoesDashboard from "../../other/AcoesDashboard";

import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCheckCircle, faChevronDown, faChevronUp, faClock, faFileExcel, faMinus, faPlus, faSearchPlus, faTimesCircle} from "@fortawesome/free-solid-svg-icons";
import {Chart, Doughnut} from "react-chartjs-2";

import connection from "../../connection";
import general from "../../general";

import formacaoContinuaStyle from "assets/jss/material-dashboard-pro-react/views/formacaoContinuaStyle.jsx";

// ---- Percursos ----

const ESTADO_CONFIG = {
  APTO: {label: "APTO", styleKey: "estadoApto", icon: faCheckCircle},
  NAO_APTO: {label: "NÃO APTO", styleKey: "estadoNaoApto", icon: faTimesCircle},
  EM_PROGRESSO: {label: "EM PROGRESSO", styleKey: "estadoInscrito", icon: faClock},
  TERMINADO: {label: "TERMINADO", styleKey: "estadoTerminado", icon: faCheckCircle}
};
const PERCURSOS_CODIGOS = ["PEI", "PEII", "PEIII", "PEIV", "PGL"];
const PERCURSOS_PE_CODIGOS = ["PEI", "PEII", "PEIII", "PEIV"];

function estadoBadge(estado, classes) {
  const config = ESTADO_CONFIG[estado];
  if (!config) return null;
  return (
    <span className={`${classes.estadoBadge} ${classes[config.styleKey]}`}>
      {config.icon && <FontAwesomeIcon icon={config.icon}/>}
      {config.label}
    </span>
  );
}

function EstadoPill({label, pillClass, classes}) {
  return <span className={`${classes.estadoPill} ${classes[pillClass]}`}>{label}</span>;
}

// Override "não apto" quando percurso bloqueado e grupo não é Tronco Comum.
function pillParaModulo(modulo) {
  const codigo = modulo.resultado.codigo;
  if (codigo === "OK") return {label: "concluído", pillClass: "estadoPillConcluido"};
  if (["PEND", "PEND_FEEDBACK", "PROG"].includes(codigo)) return {label: "pendente", pillClass: "estadoPillPendente"};
  return {label: modulo.obrigatorio ? "não inscrito" : "opcional", pillClass: "estadoPillNaoInscrito"};
}

function GrupoModulos({grupo, percursoBloqueado, classes}) {
  // Módulos concluídos em primeiro lugar
  grupo.modulos.sort(m => m.resultado.codigo === 'OK' ? -1 : 1);

  const modulosObrigatorios = grupo.modulos.filter(m => m.obrigatorio);
  const modulosOpcionais = grupo.modulos.filter(m => !m.obrigatorio);

  return [modulosObrigatorios, modulosOpcionais].map((moduloSet, idx) => <div>
    {modulosObrigatorios.length > 0 && modulosOpcionais.length ? <h6>{idx === 0 ? ('Obrigatórios (' + modulosObrigatorios.length + ')') : ('Opcionais' + (grupo.minimo_opcionais ? (" (mínimo " + (grupo.descritivo_minimo_opcionais || grupo.minimo_opcionais)) + ")" : ""))}</h6> : null}
    {moduloSet.map((modulo, idx) => {
      const {label, pillClass} = pillParaModulo(modulo);
      return (
        <div key={idx} className={classes.moduloRow}>
          <div className={classes.moduloIcon}>
            <EstadoParticipacaoModulo estado={modulo}/>
          </div>
          <span className={classes.moduloNome}>{modulo.modulo}</span>
          <EstadoPill label={label} pillClass={pillClass} classes={classes}/>
        </div>
      );
    })}
  </div>);
}

function PercursoPanel({percurso, classes}) {
  const grupos = percurso.estado_modulos || [];
  let idx_incompleto = grupos?.findIndex(g => g.modulos_completos < g.modulos_total);
  const [activeTab, setActiveTab] = useState(idx_incompleto < 0 ? grupos.length - 1 : idx_incompleto);
  const percursoBloqueado = percurso.estado === "NAO_APTO";
  const grupoAtivo = grupos[activeTab];

  return (
    <div className={classes.percursoContent}>
      {grupos.length > 0 && <div className={classes.percursoContentTop}>
        <div className={classes.tabList}>
          {grupos.map((grupo, idx) => (
            <button
              key={idx}
              type="button"
              className={`${classes.tabButton} ${idx === activeTab ? classes.tabButtonActive : ""}`}
              onClick={() => setActiveTab(idx)}
            >
              {grupo.designacao}
            </button>
          ))}
        </div>
        <span className={classes.modulosCounter}>
          {percurso.modulos_completos}/{percurso.modulos_total} módulos
        </span>
      </div>}

      {grupoAtivo && (
        <GrupoModulos
          grupo={grupoAtivo}
          percursoBloqueado={percursoBloqueado}
          classes={classes}
        />
      )}

      {percurso.estado === 'APTO' && <div className={classes.inscreverWrapper}>
        <Button
          className={classes.button + " " + classes.primary + " " + classes.buttonConfirm}
          onClick={() => { /* TODO: inscrição no percurso */

            if (['PEI', 'PEII', 'PEIII', 'PEIV'].includes(percurso.grupo_fluxo)) {
              AcoesDashboard.FORMALIZAR_PE.callback(classes);
              return;
            }

            general.warning("Entre em contacto com o secretário regional para participar neste percurso");
          }}
        >
          Inscrever no Percurso
        </Button>
      </div>}

      {percurso.modulos_em_falta > 0 && percursoBloqueado && (
        <div className={classes.faltamModulos}>
          Faltam <strong>{percurso.modulos_em_falta} módulos</strong> para estar apto
        </div>
      )}
    </div>
  );
}

function Percursos({percursos, classes}) {
  if (!percursos) return null;

  const collapses = Object.entries(percursos).map(([codigo, percurso]) => ({
    title: (
      <div className={classes.percursoSummary}>
        <span className={classes.percursoDesignacao}>
          <strong>{codigo}</strong>|&nbsp;&nbsp;{percurso.designacao}
        </span>
        <div className={classes.progressBarContainer}>
          <LinearProgress
            variant="determinate"
            value={percurso.percentagem}
            className={classes.progressBar}
          />
          <span className={classes.percentagem}>{percurso.percentagem}%</span>
        </div>
        {estadoBadge(percurso.estado, classes)}
      </div>
    ),
    content: <PercursoPanel percurso={percurso} classes={classes}/>
  }));

  return (
    <div>
      <h3 className={classes.sectionTitle}>Percursos</h3>
      <div className={classes.sectionCard}>
        <Accordion
          collapses={collapses}
          expansionPanelProps={{className: classes.percursoPanel}}
          expansionPanelSummaryProps={{className: classes.percursoSummaryRoot}}
        />
      </div>
    </div>
  );
}

// Formato "12-13 Abr 2026" quando inicio e fim no mesmo mês, senão "12 Abr – 15 Mai 2026".
function formatarDataFormacao(formacao) {
  const inicio = formacao.data_inicio ? moment(formacao.data_inicio) : null;
  const fim = formacao.data_fim ? moment(formacao.data_fim) : null;
  if (inicio && fim) {
    // Mesmo dia: mostrar só uma data.
    if (inicio.isSame(fim, "day")) {
      return inicio.format("DD MMM YYYY");
    }
    if (inicio.isSame(fim, "month")) {
      return `${inicio.format("DD")}-${fim.format("DD MMM YYYY")}`;
    }
    return `${inicio.format("DD MMM")} – ${fim.format("DD MMM YYYY")}`;
  }
  if (inicio) return inicio.format("DD MMM YYYY");
  return "Por definir";
}

function FormacaoDetalhes({formacao, classes, onVerFormacao}) {
  const dataTexto = formatarDataFormacao(formacao);
  const preco = typeof formacao.preco_inscricao === "number"
    ? `${formacao.preco_inscricao.toFixed(2).replace(".", ",")}€`
    : (formacao.preco_inscricao || "Gratuita");

  return (
    <div className={classes.formacaoDetalhes}>
      <div className={classes.detalhesGrid}>
        <div className={classes.detalhesLinha}>
          <span className={classes.detalhesLabel}>Região</span>
          <span className={classes.detalhesValue}>{formacao.regiao || "—"}</span>
        </div>
        <div className={classes.detalhesLinha}>
          <span className={classes.detalhesLabel}>Data</span>
          <span className={classes.detalhesValue}>{dataTexto}</span>
        </div>
        <div className={classes.detalhesLinha}>
          <span className={classes.detalhesLabel}>Preço</span>
          <span className={classes.detalhesValue}>{preco}</span>
        </div>
        <div className={classes.detalhesLinha}>
          <span className={classes.detalhesLabel}>Modalidade</span>
          <span className={classes.detalhesValue}>{formacao.modalidade || "—"}</span>
        </div>
      </div>
      <div className={classes.verFormacaoWrapper}>
        <Button
          className={classes.verFormacaoButton}
          onClick={() => onVerFormacao(formacao.id)}
        >
          Ver Formação
        </Button>
      </div>
    </div>
  );
}

function FormacaoRow({formacao, isExpanded, onToggle, onVerFormacao, classes}) {
  const dataInicio = formacao.data_inicio ? moment(formacao.data_inicio) : null;
  const mesAno = dataInicio ? dataInicio.format("MMMM YYYY").replace(/^./, c => c.toUpperCase()) : "";
  const dataRange = formatarDataFormacao(formacao);
  const linhaData = `${dataRange}${formacao.modalidade ? ` · ${formacao.modalidade}` : ""}`;

  return (
    <div>
      <div className={classes.formacaoRow}>
        <div className={classes.formacaoRowText}>
          <div>{formacao.regiao}{mesAno ? ` | ${mesAno}` : ""}</div>
          <div>{linhaData}</div>
        </div>
        <button
          type="button"
          className={classes.formacaoToggle}
          onClick={onToggle}
          aria-label={isExpanded ? "Esconder detalhes" : "Mostrar detalhes"}
        >
          <FontAwesomeIcon icon={isExpanded ? faMinus : faPlus}/>
        </button>
      </div>
      {isExpanded && (
        <FormacaoDetalhes
          formacao={formacao}
          classes={classes}
          onVerFormacao={onVerFormacao}
        />
      )}
    </div>
  );
}

function ModuloDetalhes({formacoes, classes, onVerFormacao}) {
  const [expandedId, setExpandedId] = useState(null);

  if (!formacoes || formacoes.length === 0) {
    return (
      <div className={classes.moduloContent}>
        <div className={classes.catalogoVazio}>Sem datas disponíveis.</div>
      </div>
    );
  }

  return (
    <div className={classes.moduloContent}>
      <div className={classes.datasDisponiveisTitulo}>Datas Disponíveis</div>
      {formacoes.map(formacao => (
        <FormacaoRow
          key={formacao.id}
          formacao={formacao}
          isExpanded={expandedId === formacao.id}
          onToggle={() => setExpandedId(expandedId === formacao.id ? null : formacao.id)}
          onVerFormacao={onVerFormacao}
          classes={classes}
        />
      ))}
    </div>
  );
}

// Deriva os módulos a apresentar no catálogo a partir do estado de formação contínua.
// Segue a regra de visibilidade do PercursoPanel: se TC por concluir, só módulos do TC;
// se TC concluído, módulos de Específica + Técnica de todos os percursos, sem duplicados.
// Módulos concluídos ontinuam visíveis.
// Devolve Map<id_modulo, {id_modulo, resultado, percursos: string[]}>.
function derivarModulosRelevantes(percursos) {
  if (!percursos) return new Map();

  // O TC é partilhado entre PEs — basta detetar uma conclusão.
  let tcCompleto = false;
  for (const p of Object.values(percursos)) {
    const tc = (p.estado_modulos || []).find(g => g.designacao === "Tronco Comum");
    if (tc && tc.modulos_total > 0 && tc.modulos_completos === tc.modulos_total) {
      tcCompleto = true;
      break;
    }
  }

  const relevantes = new Map();
  for (const [codigoPercurso, percurso] of Object.entries(percursos)) {
    for (const grupo of percurso.estado_modulos || []) {
      for (const modulo of grupo.modulos || []) {
        const existing = relevantes.get(modulo.id_modulo);
        if (existing) {
          if (!existing.percursos.includes(codigoPercurso)) {
            existing.percursos.push(codigoPercurso);
          }
          // Obrigatório se for obrigatório em qualquer percurso.
          existing.obrigatorio = existing.obrigatorio || !!modulo.obrigatorio;
        } else {
          relevantes.set(modulo.id_modulo, {
            id_modulo: modulo.id_modulo,
            resultado: modulo.resultado,
            obrigatorio: !!modulo.obrigatorio,
            percursos: [codigoPercurso]
          });
        }
      }
    }
  }
  return relevantes;
}

function ModuloCatalogoRow({mod, classes, isExpanded, onToggle}) {
  const n = mod.numDatas;
  const corDot = n > 0 ? classes.moduloMetaDotVerde : classes.moduloMetaDotCinza;
  const txtDatas = n === 0 ? "Sem Datas" : `${n} ${n === 1 ? "Data Disponível" : "Datas Disponíveis"}`;
  const {label, pillClass} = pillParaModulo({resultado: mod.resultado});
  const obrigatoriedade = mod.obrigatorio ? "Obrigatório" : "Opcional";

  return (
    <div
      className={classes.catalogoRow}
      onClick={onToggle}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onToggle();
        }
      }}
    >
      <div className={classes.catalogoRowMain}>
        <div className={`${classes.catalogoRowTitulo}${isExpanded ? ` ${classes.catalogoRowTituloExpandido}` : ""}`}>
          <strong>{mod.codigo}</strong>&nbsp;|&nbsp;{mod.designacao}
        </div>
        <div className={classes.catalogoRowMeta}>
          <span>{obrigatoriedade}</span>
          <span className={classes.catalogoMetaSep}>·</span>
          <span style={{display: "flex", alignItems: "center"}}>
            <span className={`${classes.moduloMetaDot} ${corDot}`}/>
            {txtDatas}
          </span>
          <span className={classes.catalogoMetaSep}>·</span>
          <EstadoPill label={label} pillClass={pillClass} classes={classes}/>
        </div>
      </div>
      {/* Chevron também alterna; stopPropagation evita duplo-toggle via row. */}
      <button
        type="button"
        className={classes.catalogoChevron}
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
        aria-label={isExpanded ? "Esconder detalhes" : "Mostrar detalhes"}
        tabIndex={-1}
      >
        <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown}/>
      </button>
    </div>
  );
}

// Mapeia o código do resultado para o grupo usado pelo filtro estado_participacao.
// Manter em sincronia com pillParaModulo.
function grupoEstadoModulo(codigo) {
  if (codigo === "OK") return "concluido";
  if (["PEND", "PEND_FEEDBACK", "PROG"].includes(codigo)) return "pendente";
  return "nao_inscrito";
}

function CatalogoModulos({modulos, percursos, classes, onVerFormacao}) {
  const [filtros, setFiltros] = useState({});
  const [pesquisa, setPesquisa] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(8);
  const [formacoesPagina, setFormacoesPagina] = useState([]);
  const [expandedId, setExpandedId] = useState(null);

  const modulosRelevantes = useMemo(
    () => derivarModulosRelevantes(percursos),
    [percursos]
  );

  // Enriquece os módulos com codigo/designação; descarta os não relevantes.
  const modulosEnriched = useMemo(() => {
    if (!modulos) return [];
    const out = [];
    for (const m of modulos) {
      const rel = modulosRelevantes.get(m.id);
      if (!rel) continue;
      out.push({
        id_modulo: m.id,
        codigo: m.codigo,
        designacao: m.designacao,
        resultado: rel.resultado,
        obrigatorio: rel.obrigatorio,
        percursos: rel.percursos
      });
    }
    return out;
  }, [modulos, modulosRelevantes]);

  // Aplica filtros cliente.
  const modulosFiltrados = useMemo(() => {
    const selPercurso = filtros.tipo_percurso || [];
    const selEstado = filtros.estado_participacao || [];
    const pesquisaNormalizada = pesquisa.trim().toLowerCase();
    return modulosEnriched.filter(m => {
      if (selPercurso.length > 0) {
        const intersects = m.percursos.some(p => selPercurso.includes(p));
        if (!intersects) return false;
      }
      if (selEstado.length > 0) {
        const grupo = grupoEstadoModulo(m.resultado?.codigo);
        if (!selEstado.includes(grupo)) return false;
      }
      if (pesquisaNormalizada) {
        const textoPesquisa = `${m.codigo || ""} ${m.designacao || ""}`.toLowerCase();
        if (!textoPesquisa.includes(pesquisaNormalizada)) return false;
      }
      return true;
    });
  }, [modulosEnriched, filtros, pesquisa]);

  const totalPages = Math.max(1, Math.ceil(modulosFiltrados.length / pageSize));
  const currentPage = Math.min(page, totalPages - 1);
  const pageStart = currentPage * pageSize;
  const pageModulos = modulosFiltrados.slice(pageStart, pageStart + pageSize);

  // Chave estável para fetch das datas disponíveis.
  const pageIdsKey = pageModulos.map(m => m.id_modulo).join(",");

  // Carrega datas disponíveis só para os módulos visíveis na página actual.
  useEffect(() => {
    const ids = pageIdsKey ? pageIdsKey.split(",").map(Number) : [];
    if (ids.length === 0) {
      setFormacoesPagina([]);
      return;
    }
    connection.get(
      "formacao/listagem_disponiveis",
      {filtragem: {modulos: ids}},
      (data) => setFormacoesPagina(data?.formacoes_disponiveis || []),
      () => setFormacoesPagina([])
    );
  }, [pageIdsKey]);

  // Repõe página e expansão ao alterar filtros.
  useEffect(() => {
    setPage(0);
    setExpandedId(null);
  }, [filtros, pesquisa]);

  const aoAtualizarFiltros = (filtragem, filtroSearch = "") => {
    const out = {...(filtragem || {})};
    for (let key in out)
      if (!out[key] || out[key].length === 0) delete out[key];
    setFiltros(out);
    setPesquisa(filtroSearch);
  };

  const moduloFormacoes = (id_modulo) =>
    formacoesPagina.filter(f => f.modulo_id === id_modulo || f.id_modulo === id_modulo);

  // Shim de compatibilidade: o payload histórico usa "modulo: CODIGO | Designação".
  const moduloFormacoesByLabel = (codigo, designacao) =>
    formacoesPagina.filter(f => f.modulo === `${codigo} | ${designacao}`);

  const getFormacoesForModulo = (mod) => {
    const porId = moduloFormacoes(mod.id_modulo);
    if (porId.length > 0) return porId;
    return moduloFormacoesByLabel(mod.codigo, mod.designacao);
  };

  const tableData = pageModulos.map(mod => {
    const formacoes = getFormacoesForModulo(mod);
    const numDatas = formacoes.length;
    return {
      ...mod,
      formacoes,
      numDatas,
      estadoGrupo: grupoEstadoModulo(mod.resultado?.codigo)
    };
  });

  // Índice da linha expandida; -1 se não visível na página actual.
  const expandedIdx = expandedId != null
    ? tableData.findIndex(r => r.id_modulo === expandedId)
    : -1;

  // Coluna única: toda a linha é gerida por ModuloCatalogoRow.
  const columns = [
    {
      Header: "",
      Cell: (row) => (
        <ModuloCatalogoRow
          mod={row.original}
          classes={classes}
          isExpanded={expandedId === row.original.id_modulo}
          onToggle={() => setExpandedId(expandedId === row.original.id_modulo ? null : row.original.id_modulo)}
        />
      )
    }
  ];

  return (
    <ProviderFiltrosAcontecimento
      acontecimento="formacao"
      atualizar={aoAtualizarFiltros}
      lista_filtros={["tipo_percurso", "estado_participacao"]}
    >
      <Card style={{marginTop: "2.6rem"}}>
        <CardHeader color="primary">
          <div style={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
            <h4 style={{flex: 1}}>Catálogo de Módulos</h4>
            <WidgetsFiltrosAcontecimento/>
          </div>
        </CardHeader>
        <CaixaFiltrosAcontecimento/>
        {modulosEnriched.length === 0 ? (
          <div className={classes.catalogoVazio}>Nenhum módulo a apresentar.</div>
        ) : modulosFiltrados.length === 0 ? (
          <div className={classes.catalogoVazio}>Nenhum módulo corresponde aos filtros selecionados.</div>
        ) : (
          <ReactTable
            data={tableData}
            columns={columns}
            minRows={1}
            pageSize={pageSize}
            page={currentPage}
            pages={totalPages}
            manual
            // onPageChange: caminho directo da paginação → estado.
            onPageChange={(p) => {
              setPage(p);
              setExpandedId(null);
            }}
            // onFetchData: apenas para alterações de pageSize.
            onFetchData={(state) => {
              if (state.pageSize !== pageSize) setPageSize(state.pageSize);
            }}
            showPaginationBottom={true}
            sortable={false}
            className={`-striped -highlight ${classes.catalogoTable}`}
            expanded={expandedIdx >= 0 ? {[expandedIdx]: true} : {}}
            SubComponent={(row) => <ModuloDetalhes
              formacoes={row.original.formacoes}
              classes={classes}
              onVerFormacao={onVerFormacao}
            />}
          />
        )}
      </Card>
    </ProviderFiltrosAcontecimento>
  );
}

// ---- Dirigentes — Gráficos ----

let pluginCentroDoughnutRegistado = false;

function registarPluginCentroDoughnut() {
  if (pluginCentroDoughnutRegistado) return;
  Chart.pluginService.register({
    afterDraw: (chart) => {
      if (chart.config.type !== "doughnut") return;
      const dataset = chart.data.datasets && chart.data.datasets[0];
      if (!dataset || !dataset.data || !dataset.data.length) return;
      const total = dataset.data.reduce((a, b) => a + (Number(b) || 0), 0);
      const {ctx, width, height} = chart;
      const centerX = width / 2;
      const centerY = height / 2;
      ctx.save();
      ctx.textAlign = "center";
      ctx.fillStyle = "#3C4858";
      ctx.font = "13px sans-serif";
      ctx.textBaseline = "bottom";
      ctx.fillText("Dirigentes", centerX, centerY);
      ctx.font = "bold 16px sans-serif";
      ctx.textBaseline = "top";
      ctx.fillText(total, centerX, centerY + 2);
      ctx.restore();
    }
  });
  pluginCentroDoughnutRegistado = true;
}

const DIRIGENTES_FLUXO_COLORS = {
  inscritos: "#00923F",
  tronco_comum: "#FFAE00",
  formacao_especifica: "#E77AFF",
  formacao_tecnica: "#00AEC4"
};

function GraficoDirigentes({title, segments, classes, cumulative = false}) {
  const total = segments.reduce((sum, s) => sum + (Number(s.value) || 0), 0);
  const displayValues = cumulative
    ? segments.map((_, i) => segments.slice(i).reduce((sum, s) => sum + (Number(s.value) || 0), 0))
    : segments.map(s => Number(s.value) || 0);
  const data = {
    labels: segments.map(s => s.label),
    datasets: [{
      data: segments.map(s => s.value),
      backgroundColor: segments.map(s => s.color),
      borderWidth: 0,
      hoverBorderWidth: 0
    }]
  };
  const options = {
    cutoutPercentage: 60,
    legend: {display: false},
    tooltips: {enabled: true},
    maintainAspectRatio: true
  };

  return (
    <div className={classes.dirigentesCard}>
      <div className={classes.dirigentesCardTitle}>{title}</div>
      <div className={classes.dirigentesChartContainer}>
        <Doughnut data={data} options={options}/>
      </div>
      <div className={classes.dirigentesLegend}>
        {segments.map((s, i) => {
          const pct = total > 0 ? Math.round((s.value / total) * 100) : 0;
          return (
            <React.Fragment key={i}>
              {i > 0 && <div className={classes.dirigentesLegendDivider}/>}
              <div className={classes.dirigentesLegendItem}>
                <span className={classes.dirigentesLegendDot} style={{backgroundColor: s.color}}/>
                <span className={classes.dirigentesLegendLabel}>{s.label}</span>
                <span className={classes.dirigentesLegendValues}>
                  <span style={{color: s.color, fontWeight: 700}}>{displayValues[i]}</span>
                  <span className={classes.dirigentesLegendSep}>|</span>
                  <span style={{fontWeight: 700}}>{pct}%</span>
                </span>
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

function GraficosDirigentes({classes}) {

  const [estatisticas, setEstatisticasDirigentes] = useState(null);
  const [loading, setEstatisticasDirigentesLoading] = useState(false);

  registarPluginCentroDoughnut();

  useEffect(() => {
    setEstatisticasDirigentesLoading(true);
    connection.get("percurso/continuos/estatisticas_dirigentes", {}).then(data => {
      setEstatisticasDirigentes(data)
    }).then(() => {
      setEstatisticasDirigentesLoading(false);
    });
  }, []);

  if (loading) {
    return (
      <div className={classes.dirigentesSection}>
        <h3 className={classes.sectionTitle}>Dirigentes</h3>
        <div style={{textAlign: "center", padding: 20, color: "#888"}}>A carregar estatísticas…</div>
      </div>
    );
  }

  if (!estatisticas) return null;

  const {creditos, pei, peii, peiii, peiv, pgl} = estatisticas;
  if (!creditos || !pei || !peii || !peiii || !peiv || !pgl) return null;

  const peCharts = [
    {title: "PEI", stats: pei},
    {title: "PEII", stats: peii},
    {title: "PEIII", stats: peiii},
    {title: "PEIV", stats: peiv}
  ];

  return (
    <div className={classes.dirigentesSection}>
      <h3 className={classes.sectionTitle}>Dirigentes</h3>
      <Card className={classes.dirigentesOuterCard}>
        <CardBody className={classes.dirigentesOuterCardBody}>
          <GridContainer>
            <GridItem xs={12} sm={6} md={4} className={classes.dirigentesGridItem}>
              <GraficoDirigentes
                title="Créditos"
                classes={classes}
                segments={[
                  {label: "Créditos em dia", value: creditos.em_dia, color: "#00923F"},
                  {label: "Créditos em atraso", value: creditos.em_atraso, color: "#C70000"}
                ]}
              />
            </GridItem>
            {peCharts.map(({title, stats}) => (
              <GridItem key={title} xs={12} sm={6} md={4} className={classes.dirigentesGridItem}>
                <GraficoDirigentes
                  title={title}
                  classes={classes}
                  cumulative
                  segments={[
                    {label: "Têm Tronco Comum", value: stats.tronco_comum, color: DIRIGENTES_FLUXO_COLORS.tronco_comum},
                    {label: "Em fluxo", value: stats.inscritos, color: DIRIGENTES_FLUXO_COLORS.inscritos},
                    {label: "Têm Formação Específica", value: stats.formacao_especifica, color: DIRIGENTES_FLUXO_COLORS.formacao_especifica},
                    {label: "Têm Formação Técnica", value: stats.formacao_tecnica, color: DIRIGENTES_FLUXO_COLORS.formacao_tecnica}
                  ]}
                />
              </GridItem>
            ))}
            <GridItem xs={12} sm={6} md={4} className={classes.dirigentesGridItem}>
              <GraficoDirigentes
                title="PGL"
                classes={classes}
                cumulative
                segments={[
                  {label: "Têm EMS", value: pgl.tem_ems, color: DIRIGENTES_FLUXO_COLORS.formacao_tecnica},
                  {label: "Em fluxo", value: pgl.inscritos, color: DIRIGENTES_FLUXO_COLORS.inscritos},
                  {label: "Têm Formação Específica", value: pgl.formacao_especifica, color: DIRIGENTES_FLUXO_COLORS.formacao_especifica}
                ]}
              />
            </GridItem>
          </GridContainer>
        </CardBody>
      </Card>
    </div>
  );
}

// ---- Dirigentes — Lista ----

function DirigenteDetalhe({percursos_detalhe, classes}) {
  const percursos = percursos_detalhe?.percursos || {};
  const percursosVisiveis = PERCURSOS_CODIGOS
    .filter(codigo => percursos[codigo])
    .map(codigo => ({codigo, detalhe: percursos[codigo]}));

  if (percursosVisiveis.length === 0) {
    return <div className={classes.listaVazia}>Sem dados de percursos.</div>;
  }

  return (
    <div className={classes.listaExpanded}>
      {percursosVisiveis.map(({codigo, detalhe}) => {
        return (
          <div key={codigo} className={classes.listaExpandedRow}>
            <span className={classes.listaExpandedCodigo}>
              <strong>{codigo}</strong>|&nbsp;&nbsp;{detalhe.designacao || codigo}
            </span>
            {typeof detalhe.percentagem === "number" && (
              <div className={classes.listaExpandedProgresso}>
                <LinearProgress
                  variant="determinate"
                  value={detalhe.percentagem}
                  className={classes.progressBar}
                />
                <span className={classes.listaExpandedPercentagem}>{detalhe.percentagem}%</span>
              </div>
            )}
            <span className={classes.listaExpandedModulos}>
              {typeof detalhe.modulos_completos === "number" && typeof detalhe.modulos_total === "number"
                ? `${detalhe.modulos_completos}/${detalhe.modulos_total} Módulos Completos`
                : "Sem progresso disponível"}
            </span>
            {estadoBadge(detalhe.estado, classes)}
          </div>
        );
      })}
    </div>
  );
}

function ListaDirigentes({classes}) {
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const [state, setState] = useReducer(
    (state, newState) => ({...state, ...newState}),
    {
      filter: {},
      search: "",
      data: {users: []},
      page: 0,
      pageSize: 12
    });

  const debouncedFilter = useDebounce(state.search, 500);

  const aoAtualizarFiltros = (filtro, pesquisa) => {
    let filtragem = {...filtro}

    for (let key in filtragem)
      if (!filtragem[key] || filtragem[key].length === 0) delete filtragem[key];

    setState({
      page: 0,
      filter: filtragem,
      search: pesquisa
    });
  };

  const getPercursosAtivos = (estado = {}) => {
    const percursos = estado.percursos || {};
    return PERCURSOS_CODIGOS.filter(codigo => percursos[codigo]?.estado === "EM_PROGRESSO");
  };

  useEffect(() => {
    setLoading(true);
    const params = {
      filter: state.search,
      filtro: state.filter,
      numero_resultados: state.pageSize,
      pagina: state.page
    };

    connection.get("percurso/continuos/listagem_dirigentes", params, ({users, paginacao}) => {
      setState({
        data: {
          paginacao,
          users
        }
      })
      setLoading(false);
      setExpandedId(null);
    }, () => {
      setLoading(false);
    });
  }, [state.page, state.filter, debouncedFilter]);

  const onExportar = () => {
    window.open(connection.getFullUrl("percurso/continuos/listagem_dirigentes", {
      exportar: "1",
      filter: debouncedFilter.trim(),
      filtro: state.filter
    }), "_blank");
  };

  const expandedIdx = expandedId != null
    ? state.data.users.findIndex(r => r.id === expandedId)
    : -1;

  const rows = state.data.users.map(row => ({
    ...row,
    percursos: getPercursosAtivos(row.estado_detalhe)
  }));

  const columns = [
    {
      Header: "Dirigente",
      accessor: "nome",
      Cell: (row) => (
        <span>
          <EstadoUtilizador nome={row.value} estado={row.original.estado}/>
          <span style={{fontSize: 10, display: "block", marginTop: -3, color: "#5e5e5e"}}>
            {[row.original.agrupamento, row.original.nucleo, row.original.regiao].filter(Boolean).join(" / ")}
          </span>
        </span>
      )
    },
    {
      Header: "NIN",
      accessor: "NIN",
      maxWidth: 160
    },
    {
      Header: "Percurso",
      accessor: "percursos",
      sortable: false,
      Cell: (row) => row.value?.join(", ") || "—"
    },
    {
      Header: "Créditos",
      accessor: "creditos",
      maxWidth: 150,
      Cell: (row) => {
        const creditos = row.value || 0;
        const creditosTexto = `${creditos} ${creditos === 1 ? "crédito" : "créditos"}`;
        const creditosClass = Number(row.original.creditos_em_falta || 0) === 0 ? "estadoPillConcluido" : "estadoPillNaoApto";
        return (
          <span
            className={`${classes.estadoPill} ${classes[creditosClass]}`}
            style={{fontSize: 12, padding: "6px 14px", minWidth: 86, display: "inline-block", textAlign: "center"}}
          >
            {creditosTexto}
          </span>
        );
      }
    },
    {
      Header: "",
      sortable: false,
      filterable: false,
      maxWidth: 90,
      Cell: (row) => {
        const expandido = expandedId === row.original.id;
        return (
          <div className="actions-right">
            <Button
              justIcon
              round
              simple
              onClick={(e) => {
                e.stopPropagation();
                setExpandedId(expandido ? null : row.original.id);
              }}
              color="primary"
            >
              <FontAwesomeIcon icon={expandido ? faChevronUp : faChevronDown}/>
            </Button>
            <Button
              justIcon
              round
              simple
              onClick={(e) => {
                e.stopPropagation();
                window.App.hist.push(`/perfil/${row.original.id}`);
              }}
              color="info"
            >
              <FontAwesomeIcon icon={faSearchPlus}/>
            </Button>
          </div>
        );
      }
    }
  ];
  console.log(state)
  return (
    <ProviderFiltrosAcontecimento
      acontecimento="formacao"
      atualizar={aoAtualizarFiltros}
      lista_filtros={["tipo_utilizador", "regiao", "nucleo", "agrupamento", "tipo_percurso", "creditos", "elegibilidade"]}
    >
      <div className={classes.listaDirigentesSection}>
        <Card>
          <CardHeader color="primary">
            <div style={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
              <h4 style={{flex: 1}}>Lista de Dirigentes</h4>
              <div style={{display: "flex", alignItems: "center", gap: 12}}>
                <WidgetsFiltrosAcontecimento/>
                <Button className={classes.listaExportButton} onClick={onExportar}>
                  <span className={classes.listaExportButtonInner}>
                    <FontAwesomeIcon icon={faFileExcel}/>
                    Exportar para Excel
                  </span>
                </Button>
              </div>
            </div>
          </CardHeader>
          <CaixaFiltrosAcontecimento/>
          <ReactTable
            data={rows}
            columns={columns}
            loading={loading}
            manual
            minRows={1}
            page={state.page}
            pageSize={12}
            defaultPageSize={12}
            pages={state.data?.paginacao?.paginas}
            showPageSizeOptions={false}
            onPageChange={(nextPage) => {
              setState({page: nextPage});
              setExpandedId(null);
            }}
            showPaginationBottom={true}
            sortable={false}
            className="-striped -highlight"
            expanded={expandedIdx >= 0 ? {[expandedIdx]: true} : {}}
            getTrProps={(state, rowInfo) => {
              if (!rowInfo) return {};
              return {
                onClick: () => setExpandedId(expandedId === rowInfo.original.id ? null : rowInfo.original.id),
                style: {cursor: "pointer"}
              };
            }}
            SubComponent={(row) => (
              <DirigenteDetalhe
                percursos_detalhe={{percursos: row.original.estado_detalhe?.percursos || {}}}
                classes={classes}
              />
            )}
            placeholder="Nenhum dirigente corresponde aos filtros."
          />
        </Card>
      </div>
    </ProviderFiltrosAcontecimento>
  );
}

function Creditos({cicloAtivo, eFormador, classes}) {
  if (!cicloAtivo) return null;

  return (
    <GridContainer style={{marginBottom: 10}}>
      <GridItem xs={12} sm={eFormador ? 3 : 4}>
        <Card className={classes.creditosCard}>
          <CardBody>
            <span>Os Meus Créditos</span>
            <h4>{cicloAtivo.creditos}</h4>
          </CardBody>
        </Card>
      </GridItem>
      {eFormador && (
        <GridItem xs={12} sm={3}>
          <Card className={classes.creditosCard}>
            <CardBody>
              <span>Créditos Formação Dinamizada</span>
              <h4>{cicloAtivo.creditos_como_formador}</h4>
            </CardBody>
          </Card>
        </GridItem>
      )}
      <GridItem xs={12} sm={eFormador ? 3 : 4}>
        <Card className={classes.creditosCard}>
          <CardBody>
            <span>Créditos em Falta</span>
            <h4>{cicloAtivo.creditos_em_falta}</h4>
          </CardBody>
        </Card>
      </GridItem>
      <GridItem xs={12} sm={eFormador ? 3 : 4}>
        <Card className={classes.creditosCard}>
          <CardBody>
            <span>Fim do Ciclo</span>
            <h4>{moment(cicloAtivo.fim).format("DD [de] MMM, YYYY")}</h4>
          </CardBody>
        </Card>
      </GridItem>
    </GridContainer>
  );
}

class FormacaoContinua extends React.Component {

  constructor(props) {
    super(props);
    this.state = {
      formacao_continua: null,
      loading: true,
      catalogo_modulos: null
    };
  }

  componentDidMount() {
    this.reloadDados();
    this.reloadModulos();
  }

  reloadDados = () => {
    let id = window.App.state.user.id;
    connection.get("utilizador/perfil", {id}, (data) => {
      if (!data || !data.formacao_continua) {
        this.setState({loading: false});
        return;
      }
      this.setState({
        formacao_continua: data.formacao_continua,
        loading: false,
      });
    });
  };

  reloadModulos = () => {
    connection.get("modulo/listagem", {continuos_apenas: true}, (data) => {
      const modulos = Array.isArray(data) ? data : [];
      this.setState({catalogo_modulos: modulos});
    }, () => {
      this.setState({catalogo_modulos: []});
    });
  };

  abrirFormacaoModal = (id_formacao) => {
    const {classes} = this.props;
    window.App.openModal(
      <SweetAlert
        showClose
        style={{width: 1200, padding: 40, backgroundColor: "#EEEEEE"}}
        onConfirm={() => window.App.hideModal()}
        onCancel={() => window.App.hideModal()}
        confirmBtnText="Fechar"
        confirmBtnCssClass={classes.button + " " + classes.primary + " " + classes.buttonConfirm}
      >
        <PaginaFormacao id_formacao={id_formacao} visualizacao/>
      </SweetAlert>
    );
  };

  render() {
    const {classes} = this.props;
    const {formacao_continua, catalogo_modulos} = this.state;
    const cicloAtivo = formacao_continua ? formacao_continua.ciclo_ativo : null;
    const eFormador = general.userHasType("formador", "diretor_formacao");
    const podeGerirDirigentes = general.userHasPermission("formacao_continua.ler");

    return (
      <div>
        <Creditos cicloAtivo={cicloAtivo} eFormador={eFormador} classes={classes}/>

        <GridContainer style={{marginBottom: 10}}>
          {formacao_continua && (
            <GridItem xs={12} md={8}>
              <Percursos
                percursos={formacao_continua.percursos}
                classes={classes}
              />
            </GridItem>
          )}
          {formacao_continua && (
            <GridItem xs={12} md={4}>
              <CatalogoModulos
                modulos={catalogo_modulos}
                percursos={formacao_continua.percursos}
                classes={classes}
                onVerFormacao={this.abrirFormacaoModal}
              />
            </GridItem>
          )}
        </GridContainer>

        {podeGerirDirigentes && (
          <GraficosDirigentes classes={classes}/>
        )}

        {podeGerirDirigentes && (
          <ListaDirigentes classes={classes}/>
        )}
      </div>
    );
  }
}

export default withStyles(formacaoContinuaStyle)(FormacaoContinua);
